import { useMutation, useQueryClient } from "@tanstack/react-query";

import { processRequest } from "@api/config";
import { remarksAPI } from "@api/remarks";
import type { TSortField, TSortOrder } from "@api/remarks/request.types";

import { useAuthQuery } from "@lib/queryClient";

import { API_PREFIXES, QUERY_KEYS } from "@configs/api";

type TUseGetRemarkListQueryParams = {
	offset: number;
	limit: number;
	sortOrder: TSortOrder;
	sortField: TSortField;
	query: string;
};

export const useGetRemarkListQuery = ({ offset, limit, sortOrder, sortField, query }: TUseGetRemarkListQueryParams) =>
	useAuthQuery({
		queryKey: [QUERY_KEYS.REMARK_LIST, sortOrder, sortField, offset, query],
		queryFn: async () =>
			processRequest({
				prefix: API_PREFIXES.GET_REMARK_LIST,
				request: (token) => remarksAPI.getRemarkList({ token, offset, limit, sortOrder, sortField, query }),
			}),
	});

export const useUpdateRemarkMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ userId, remark }: { userId: string; remark: string }) =>
			processRequest({
				prefix: API_PREFIXES.UPDATE_REMARK,
				request: (token) => remarksAPI.updateRemark({ userId, remark, token }),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REMARK_LIST] });
		},
	});
};

export const useCreateRemarkMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ userId, remark }: { userId: string; remark: string }) =>
			processRequest({
				prefix: API_PREFIXES.CREATE_REMARK,
				request: (token) => remarksAPI.createRemark({ userId, remark, token }),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REMARK_LIST] });
		},
	});
};

export const useDeleteRemarkMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id }: { id: string }) =>
			processRequest({
				prefix: API_PREFIXES.DELETE_REMARK,
				request: (token) => remarksAPI.deleteRemark({ id, token }),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REMARK_LIST] });
		},
	});
};
